import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, Calendar, Users, TrendingUp, Search, Car, PlusCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ThiriMascot } from "@/components/ThiriMascot";
import { RideCard } from "@/components/RideCard";
import { PostCard } from "@/components/PostCard";

export default function Home() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/home/summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return (
    <div className="space-y-6" data-testid="home-skeleton">
      <div className="skeleton-shimmer h-9 w-64 rounded-2xl" />
      <div className="skeleton-shimmer h-16 w-full rounded-full" />
      <div className="grid grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton-shimmer h-24 rounded-2xl" />)}
      </div>
      <div className="skeleton-shimmer h-32 w-full rounded-2xl" />
      <div className="skeleton-shimmer h-40 w-full rounded-2xl" />
    </div>
  );

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-6" data-testid="home-page">
      <div>
        <h1 className="font-display font-black text-3xl">{greet}, {data.user.name.split(" ")[0]}</h1>
        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
          {data.institution && (
            <span className="flex items-center gap-1 font-semibold text-foreground">
              <BadgeCheck className="w-4 h-4 text-primary" /> {data.institution.name} verified
            </span>
          )}
          <span>· {data.buddy_count} buddies</span>
        </div>
      </div>

      <button data-testid="where-to-pill" onClick={() => navigate("/rides")}
        className="w-full flex items-center gap-4 bg-white rounded-full px-6 py-5 border border-black/5 shadow-[0_8px_32px_hsla(145,72%,40%,0.18)] text-left hover-lift active:scale-[0.98]">
        <span className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
          <Search className="w-5 h-5 text-white" />
        </span>
        <span className="text-base md:text-lg font-bold text-foreground/70">Where are you going?</span>
      </button>

      <div className="grid grid-cols-4 gap-3">
        {[
          { icon: Car, label: "Find Ride", to: "/rides", tid: "tile-find-ride" },
          { icon: PlusCircle, label: "Offer Ride", to: "/rides?tab=offer", tid: "tile-offer-ride" },
          { icon: Calendar, label: "Events", to: "/events", tid: "tile-events" },
          { icon: Users, label: "Buddies", to: "/buddies", tid: "tile-buddies" },
        ].map(({ icon: Icon, label, to, tid }) => (
          <button key={tid} data-testid={tid} onClick={() => navigate(to)}
            className="t-card p-4 flex flex-col items-center gap-2 hover-lift active:scale-95">
            <span className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </span>
            <span className="text-xs font-bold">{label}</span>
          </button>
        ))}
      </div>

      <ThiriMascot state={data.companion?.state} message={data.companion?.message}
        onAction={data.companion?.event_id ? () => navigate(`/events/${data.companion.event_id}`) : undefined}
        actionLabel="View event" />

      <section>
        <h2 className="font-display font-bold text-lg mb-3">Your upcoming ride</h2>
        {data.upcoming_ride ? (
          <RideCard ride={data.upcoming_ride} />
        ) : (
          <div className="t-card p-6 flex items-center gap-5" data-testid="no-upcoming-ride">
            <img src="https://images.unsplash.com/photo-1674216709275-61a436e1ecd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxpc29tZXRyaWMlMjBjaXR5JTIwY2FyfGVufDB8fHx8MTc4NzQ3NTMxNXww&ixlib=rb-4.1.0&q=85"
              alt="3D car illustration" className="w-24 h-24 rounded-2xl object-cover shrink-0 hidden sm:block" />
            <div className="flex-1 text-left">
              <p className="font-display font-bold">No upcoming rides yet</p>
              <p className="text-sm text-muted-foreground mt-0.5">Pool a ride with verified people from your campus.</p>
              <div className="flex gap-3 mt-3">
                <Link to="/rides" data-testid="home-find-ride" className="rounded-full bg-primary text-white px-5 py-2 text-sm font-bold active:scale-95 transition-transform duration-150">Find a ride</Link>
                <Link to="/rides?tab=offer" data-testid="home-offer-ride" className="rounded-full border-2 border-foreground px-5 py-2 text-sm font-bold hover:bg-foreground hover:text-white transition-colors duration-200">Offer a ride</Link>
              </div>
            </div>
          </div>
        )}
      </section>

      {data.buddy_activity.length > 0 && (
        <section>
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Buddy activity</h2>
          <div className="t-card divide-y divide-black/5">
            {data.buddy_activity.map((a) => (
              <Link key={`${a.event_id}-${a.user.name}-${a.text}`} to={`/events/${a.event_id}`} className="flex items-center gap-3 p-4 hover:bg-black/[0.02] transition-colors duration-200">
                <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center text-xs font-bold">{a.user.name[0]}</div>
                <p className="text-sm"><span className="font-bold">{a.user.name}</span> {a.text}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" /> Upcoming events</h2>
          <Link to="/events" data-testid="home-all-events" className="text-sm font-bold text-primary">See all</Link>
        </div>
        {data.events.length ? (
          <div className="grid sm:grid-cols-3 gap-4">
            {data.events.map((e) => (
              <Link key={e.id} to={`/events/${e.id}`} className="t-card p-4 hover:-translate-y-1 transition-transform duration-200">
                <span className="t-chip bg-primary/10 text-primary">{e.category}</span>
                <h3 className="font-display font-bold mt-2 text-sm">{e.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{e.date} · {e.time}</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="t-card p-6 text-center text-sm text-muted-foreground" data-testid="no-events">
            No events yet — be the first to <Link to="/events?create=1" className="font-bold text-primary">create one</Link>.
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> Trending on campus</h2>
          <Link to="/campus" data-testid="home-campus-link" className="text-sm font-bold text-primary">Campus feed</Link>
        </div>
        {data.trending_posts.length ? (
          <div className="space-y-4">
            {data.trending_posts.map((p) => <PostCard key={p.id} post={p} onChange={load} />)}
          </div>
        ) : (
          <div className="t-card p-6 text-center text-sm text-muted-foreground" data-testid="no-posts">
            The feed is quiet. <Link to="/campus?create=1" className="font-bold text-primary">Start the first conversation</Link>.
          </div>
        )}
      </section>
    </div>
  );
}
